
import HCLTreeMxGraphDecoder from './HCLTreeMxGraphDecoder';
import EventTreeMxGraph, {BranchJSON, EventTreeMxGraphJSON} from '../HCLTreeMxGraph/EventTreeMxGraph';
import InitVertex from '../HCLTreeNode/EventTreeNode/InitEvent/InitVertex';
import FunctionalEventVertex from '../HCLTreeNode/EventTreeNode/FunctionalEventNode/FunctionalEventVertex';
import EndStateVertex from '../HCLTreeNode/EventTreeNode/EndStateNode/EndStateVertex';
import InitVertexValue from '../HCLTreeNode/EventTreeNode/InitEvent/InitVertexValue';
import FunctionalEventVertexValue from '../HCLTreeNode/EventTreeNode/FunctionalEventNode/FunctionalEventVertexValue';
import EndStateVertexValue from '../HCLTreeNode/EventTreeNode/EndStateNode/EndStateVertexValue';
import EventTreeSuccessEdge from '../HCLTreeNode/EventTreeNode/EventTreeSuccessEdge';
import EventTreeFailureEdge from '../HCLTreeNode/EventTreeNode/EventTreeFailureEdge';
import Label from '../HCLTreeNode/HCLTreeVertexValue/Label';
import Outcome, {OutcomeJSON} from "../HCLTreeNode/HCLTreeVertexValue/Outcome";
import ReferenceTypes from "../HCLTreeNode/HCLTreeVertexValue/ReferenceTypes";
import ProxyTypes from "../HCLTreeNode/HCLTreeVertexValue/ProxyTypes"
import EventTreeVertex from "../HCLTreeNode/EventTreeNode/EventTreeVertex";
import Instruction from "../HCLTreeNode/HCLTreeVertexValue/Instruction";
import FunctionalEventPortValue from "../HCLTreeNode/EventTreeNode/FunctionalEventNode/Port/FunctionalEventPortValue";
import {mxCell} from "mxgraph-js";

/**
 * Inherits from {@link HCLTreeMxGraphDecoder} and implements codecs for {@link EventTreeMxGraph}
 */
class EventTreeMxGraphDecoder extends HCLTreeMxGraphDecoder {

  protected functionalEvents: {[index: string]: FunctionalEventVertex} = {};
  protected sequences: {[index: string]: EndStateVertex} = {};
  protected branches: {[index: string]: { outcome: BranchJSON }} = {};
  protected reverseBranchKeyMap: {[index: string]: string} = {};

  /**
   * @param {EventTreeMxGraph} graph
   * @param {EventTreeMxGraphJSON} json
   */
  constructor(graph: EventTreeMxGraph, json: EventTreeMxGraphJSON) {
    super(graph, json);
  };

  /**
   * @param {MxGraphType.mxCell} node1
   * @param {MxGraphType.mxCell} node2
   */
  protected addSuccessEdgeBetween(node1: mxCell, node2: mxCell): void {
    this.addUniqueEdge(new EventTreeSuccessEdge(), node1, node2);
  };

  /**
   * @param {MxGraphType.mxCell} node1
   * @param {MxGraphType.mxCell} node2
   */
  protected addFailureEdgeBetween(node1: mxCell, node2: mxCell): void {
    this.addUniqueEdge(new EventTreeFailureEdge(), node1, node2);
  };

  /**
   * @param {BranchJSON | OutcomeJSON} outcome
   */
  protected getVertexFromReference(outcome: BranchJSON | OutcomeJSON): EventTreeVertex {
    // if ((outcome as OutcomeJSON).tree_id) {
    //   const outcomeJSON = outcome as OutcomeJSON;
    //   // if the reference is a transfer gateTypes
    //   if (outcomeJSON.tree_id !== this.getJSON().model_tree_id) {
    //     // refName, refTreeId, refPath = '', refType, proxyTypes
    //     const etgValue = new EventTreeTransferGateVertexValue(new HCLText(outcomeJSON.name), Outcome.build(outcomeJSON));
    //     return this.addVertex(new EventTreeTransferGateVertex(outcomeJSON.name, etgValue)) as EventTreeTransferGateVertex;
    //   }
    // }

    // proxyTypes can be of type Fork and EndState
    switch (outcome._proxy) {
      case ProxyTypes.FORK:
        const branchJSON = outcome as BranchJSON;
        const forkEventVertex = this.functionalEvents[branchJSON[ReferenceTypes.FUNCTIONAL_EVENT]];

        if (forkEventVertex.visited) {
          return forkEventVertex;
        }

        forkEventVertex.visited = true;

        if (branchJSON[ReferenceTypes.PATHS].failure.instructions) {
          (forkEventVertex.getValue() as FunctionalEventVertexValue).setInstructions([new Instruction(branchJSON[ReferenceTypes.PATHS].failure.instructions[0])]);
        }

        if (forkEventVertex.successPort) {
          (forkEventVertex.successPort.getValue() as FunctionalEventPortValue).setOutcome(Outcome.build(branchJSON[ReferenceTypes.PATHS].success.outcome));
        }

        if (forkEventVertex.failurePort) {
          (forkEventVertex.failurePort.getValue() as FunctionalEventPortValue).setOutcome(Outcome.build(branchJSON[ReferenceTypes.PATHS].failure.outcome));
        }

        const paths = branchJSON[ReferenceTypes.PATHS];
        const { success, failure } = paths;
        //for success path
        if (success && success.outcome) {
          const successVertex = this.getVertexFromReference(paths.success.outcome);
          this.addSuccessEdgeBetween(forkEventVertex.successPort, successVertex);
        }

        // for failure path
        if (failure && failure.outcome) {
          const failureVertex = this.getVertexFromReference(branchJSON[ReferenceTypes.PATHS].failure.outcome);
          this.addFailureEdgeBetween(forkEventVertex.failurePort, failureVertex);
        }

        return forkEventVertex;

      case ProxyTypes.END_STATE:
        const outcomeJSON = outcome as OutcomeJSON;
        switch (outcomeJSON.reference_type) {
          case ReferenceTypes.SEQUENCES:
            return this.sequences[outcomeJSON.name];
          case ReferenceTypes.BRANCHES:
            const json = this.getJSON() as EventTreeMxGraphJSON;
            return json[ReferenceTypes.BRANCHES][outcomeJSON.name] && this.getVertexFromReference(json[ReferenceTypes.BRANCHES][outcomeJSON.name].outcome);
          default:
            throw new Error(`Unable to handle reference of type ${outcomeJSON.reference_type}`);
        }

      default:
        throw new Error(`Unable to parse outcome JSON: ${outcome}`);

    }
  };

  protected createFunctionalEvents(): void {
    const json = this.getJSON() as EventTreeMxGraphJSON;
    Object.entries(json[ReferenceTypes.FUNCTIONAL_EVENTS]).forEach((e) => {
      let value = new FunctionalEventVertexValue(new Label(e[0]), null, this.reverseBranchKeyMap[e[0]]);
      // @ts-ignore
      if (e[1].label && Object.keys(e[1].label).length) value = new FunctionalEventVertexValue(Label.build(e[1].label), null, this.reverseBranchKeyMap[e[0]]);
      const funcEvent = new FunctionalEventVertex(e[0], value);
      this.functionalEvents[e[0]] = this.addVertex(funcEvent) as FunctionalEventVertex;
    });
  };

  protected createBranches(): void {
    const json = this.getJSON() as EventTreeMxGraphJSON;
    Object.entries(json[ReferenceTypes.BRANCHES]).forEach((branchEntry) => {
      const paths = branchEntry[1].outcome[ReferenceTypes.PATHS];
      if (!paths) {
        console.error('Invalid JSON format detected. paths does not exist in', branchEntry);
      }
      this.branches[branchEntry[0]] = branchEntry[1];
      this.reverseBranchKeyMap[branchEntry[1].outcome[ReferenceTypes.FUNCTIONAL_EVENT]] = branchEntry[0];
    });
  };

  protected createSequences(): void {
    const json = this.getJSON() as EventTreeMxGraphJSON;
    Object.entries(json[ReferenceTypes.SEQUENCES]).forEach((e) => {
      let value = new EndStateVertexValue(new Label(e[0]));
      // @ts-ignore
      if (e[1].label && Object.keys(e[1].label).length) value = new EndStateVertexValue(Label.build(e[1].label));
      const endState = new EndStateVertex(e[0], value);
      this.sequences[e[0]] = this.addVertex(endState) as EndStateVertex;
    });
  };

  decode() {
    super.decode();
    this.createBranches();
    this.createFunctionalEvents();
    this.createSequences();
    const json = this.getJSON() as EventTreeMxGraphJSON;

    // just add a stub init event
    const initNode = new InitVertex(null, new InitVertexValue());
    this.addVertex(initNode);

    // start from initiating event
    const initialStateOutcome = json[ReferenceTypes.INITIAL_STATE].outcome;
    if (initialStateOutcome) {
      (initNode.getValue() as InitVertexValue).setOutcome(Outcome.build(initialStateOutcome));
      const rootNode = this.getVertexFromReference(initialStateOutcome);
      this.addEdgeBetween(initNode, rootNode);
    }

    // start from functional events with no incoming edges
    Object.values(this.functionalEvents)
      .filter(functionalEventVertex => functionalEventVertex.getIncomingEdges().length === 0)
      .forEach(functionalEventVertex => {
        this.getVertexFromReference(functionalEventVertex.toReferenceJSON());
      });

    return this.graph;
  };
}

export default EventTreeMxGraphDecoder;
